import React from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CheckCircle2, DollarSign, Wallet, PhoneIncoming, MessageSquare, TrendingUp, TrendingDown } from "lucide-react";
import { fmtMoney } from "./dashUtils";

const Trend = ({ pct, suffix = "vs prev. period" }) => {
  if (pct === null || pct === undefined) return <span className="text-[10px] text-muted-foreground/50">—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${up ? "text-emerald-600" : "text-red-500"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(pct)}% <span className="text-muted-foreground/60 font-normal">{suffix}</span>
    </span>
  );
};

const KpiCard = ({ icon: Icon, tint, label, value, sub, trend, onClick, testid, loading }) => (
  <button
    type="button"
    onClick={onClick}
    className="min-w-[150px] flex-1 snap-start rounded-xl border border-border bg-card/60 backdrop-blur-sm p-3.5 text-left transition-all hover:border-blue-500/40 hover:shadow-sm"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-7 h-7 rounded-lg ${tint} flex items-center justify-center`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <span className="text-[11px] text-muted-foreground font-medium">{label}</span>
    </div>
    {loading ? (
      <div className="h-7 w-20 rounded bg-muted animate-pulse" />
    ) : (
      <p className="text-[22px] leading-7 font-bold text-foreground font-['Manrope']">{value}</p>
    )}
    <div className="mt-0.5 min-h-[16px]">
      {trend !== undefined ? <Trend pct={trend} /> : sub ? <span className="text-[10px] text-muted-foreground">{sub}</span> : null}
    </div>
  </button>
);

const DashKpiRow = ({ kpis, financialAccess, loading }) => {
  const navigate = useNavigate();
  const k = kpis || {};
  return (
    <div className="flex gap-3 overflow-x-auto snap-x pb-1 lg:grid lg:grid-cols-6 lg:overflow-visible" data-testid="dash-kpi-row">
      <KpiCard icon={Briefcase} tint="bg-blue-500/10 text-blue-500" label="Active Jobs" loading={loading}
        value={k.active_jobs ?? 0} sub="all open statuses" onClick={() => navigate("/dashboard/jobs")} testid="kpi-active-jobs" />
      <KpiCard icon={CheckCircle2} tint="bg-emerald-500/10 text-emerald-600" label="Completed" loading={loading}
        value={k.completed_jobs ?? 0} trend={k.completed_trend} onClick={() => navigate("/dashboard/jobs")} testid="kpi-completed" />
      {financialAccess && (
        <KpiCard icon={DollarSign} tint="bg-green-500/10 text-green-600" label="Money Collected" loading={loading}
          value={fmtMoney(k.money_collected)} trend={k.collected_trend} onClick={() => navigate("/dashboard/financials")} testid="kpi-collected" />
      )}
      {financialAccess && (
        <KpiCard icon={Wallet} tint="bg-orange-500/10 text-orange-500" label="Outstanding" loading={loading}
          value={fmtMoney(k.outstanding_balance)} sub="unpaid invoice balances" onClick={() => navigate("/dashboard/invoices")} testid="kpi-outstanding" />
      )}
      <KpiCard icon={PhoneIncoming} tint="bg-violet-500/10 text-violet-500" label="New Calls" loading={loading}
        value={k.new_calls ?? 0} trend={k.calls_trend} onClick={() => navigate("/dashboard/calls")} testid="kpi-calls" />
      <KpiCard icon={MessageSquare} tint="bg-cyan-500/10 text-cyan-600" label="Unread Messages" loading={loading}
        value={k.unread_messages ?? 0} sub="open conversations" onClick={() => navigate("/dashboard/messages")} testid="kpi-unread" />
    </div>
  );
};

export default DashKpiRow;
